/**
 * Simplified date handling utilities for HousePoints
 * Uses built-in date-fns without timezone library
 */

import { format, parseISO, isValid, startOfDay, isToday, isTomorrow, isYesterday, isPast, differenceInDays, addHours } from 'date-fns'
import { getConfiguredTimezone } from './timezoneUtils'

// Cache for timezone (will be fetched from settings)
let cachedTimezone: string | null = null

/**
 * Parse any date input and return a valid Date object or null
 */
export const parseDate = (dateInput: any): Date | null => {
  if (!dateInput) return null
  
  let date: Date
  
  try {
    if (typeof dateInput === 'string') {
      // Check for obviously bad dates first
      if (dateInput.includes('1969') || dateInput.includes('1970')) {
        console.warn('Invalid date detected (1969/1970):', dateInput)
        return null
      }
      
      // Handle ISO strings
      date = parseISO(dateInput)
    } else if (dateInput instanceof Date) {
      date = dateInput
    } else if (typeof dateInput === 'number') {
      // Handle Unix timestamps (check if seconds or milliseconds)
      if (dateInput < 10000000000) {
        date = new Date(dateInput * 1000) // Convert seconds to milliseconds
      } else {
        date = new Date(dateInput)
      }
    } else {
      return null
    }
    
    // Validate the date
    if (!isValid(date)) return null
    
    // Check for obviously wrong dates (1969, etc)
    const year = date.getFullYear()
    if (year < 2020 || year > 2030) {
      console.warn('Invalid year detected:', year, 'for date:', dateInput)
      return null
    }
    
    return date
  } catch (error) {
    console.error('Date parsing error:', error, 'for input:', dateInput)
    return null
  }
}

/**
 * Format a date for display
 */
export const formatDate = (dateInput: any, formatStr: string = 'MMM d, yyyy'): string => {
  const date = parseDate(dateInput)
  if (!date) return ''  // Return empty string instead of 'Invalid date'
  
  try {
    return format(date, formatStr)
  } catch (error) {
    console.error('Date formatting error:', error)
    return ''
  }
}

/**
 * Format a date with relative text (Today, Tomorrow, Yesterday, etc.)
 * Properly handles timezone by comparing dates at start of day
 */
export const formatRelativeDate = (dateInput: any): string => {
  const date = parseDate(dateInput)
  if (!date) return ''  // Return empty string instead of 'Invalid date'
  
  try {
    // Get current date in local timezone
    const now = new Date()
    
    // Compare at start of day to avoid time issues
    const dateDay = startOfDay(date)
    const today = startOfDay(now)
    
    // Calculate days difference
    const daysDiff = differenceInDays(dateDay, today)
    
    // Return relative text based on difference
    if (daysDiff === 0) return 'Today'
    if (daysDiff === 1) return 'Tomorrow'
    if (daysDiff === -1) return 'Yesterday'
    
    // Only show as overdue if MORE than 1 day in the past
    if (daysDiff < -1) {
      if (daysDiff > -7) return format(date, 'EEEE') + ' (Overdue)' // Day name for past week
      return format(date, 'MMM d') + ' (Overdue)'
    }
    
    // Future dates
    if (daysDiff > 0 && daysDiff <= 7) return format(date, 'EEEE') // Day name for next week
    
    return format(date, 'MMM d')
  } catch (error) {
    console.error('Relative date formatting error:', error)
    return formatDate(date, 'MMM d')
  }
}

/**
 * Format time only (for schedules, etc.)
 */
export const formatTime = (dateInput: any, use24Hour: boolean = false): string => {
  const date = parseDate(dateInput)
  if (!date) return ''
  
  const formatStr = use24Hour ? 'HH:mm' : 'h:mm a'
  return formatDate(date, formatStr)
}

/**
 * Format date and time together
 */
export const formatDateTime = (dateInput: any, use24Hour: boolean = false): string => {
  const date = parseDate(dateInput)
  if (!date) return ''
  
  const timeFormat = use24Hour ? 'HH:mm' : 'h:mm a'
  return formatDate(date, `MMM d, yyyy 'at' ${timeFormat}`)
}

/**
 * Check if a date is today (comparing at start of day)
 */
export const isDateToday = (dateInput: any): boolean => {
  const date = parseDate(dateInput)
  if (!date) return false
  
  return isToday(date)
}

/**
 * Check if a date is in the past (but not today)
 */
export const isDatePast = (dateInput: any): boolean => {
  const date = parseDate(dateInput)
  if (!date) return false
  
  const dateDay = startOfDay(date)
  const today = startOfDay(new Date())
  
  return dateDay < today  // Only past if before today's start
}

/**
 * Check if a date is overdue (synchronous version using cached timezone)
 * - Past dates are overdue
 * - Today's dates are only overdue after 6pm in configured timezone
 */
export const isOverdue = (dateInput: any): boolean => {
  const date = parseDate(dateInput)
  if (!date) return false
  
  const now = new Date()
  
  // Use cached timezone or default to America/New_York
  const timezone = cachedTimezone || 'America/New_York'
  
  // Get the current time in configured timezone
  const localTime = new Date(now.toLocaleString("en-US", {timeZone: timezone}))
  const localHour = localTime.getHours()
  
  const dateDay = startOfDay(date)
  const today = startOfDay(now)
  
  // If it's before today, it's overdue
  if (dateDay < today) {
    return true
  }
  
  // If it's today, only overdue after 6pm in configured timezone
  if (dateDay.getTime() === today.getTime()) {
    return localHour >= 18 // 6pm or later
  }
  
  // Future dates are not overdue
  return false
}

/**
 * Check if a date is overdue (async version that fetches timezone from settings)
 * - Past dates are overdue
 * - Today's dates are only overdue after 6pm in configured timezone
 */
export const isOverdueAsync = async (dateInput: any): Promise<boolean> => {
  const date = parseDate(dateInput)
  if (!date) return false
  
  const now = new Date()
  
  // Get configured timezone from settings
  const timezone = await getConfiguredTimezone()
  
  // Cache it for synchronous usage
  cachedTimezone = timezone
  
  // Get the current time in configured timezone
  const localTime = new Date(now.toLocaleString("en-US", {timeZone: timezone}))
  const localHour = localTime.getHours()
  
  const dateDay = startOfDay(date)
  const today = startOfDay(now)
  
  // If it's before today, it's overdue
  if (dateDay < today) {
    return true
  }
  
  // If it's today, only overdue after 6pm in configured timezone
  if (dateDay.getTime() === today.getTime()) {
    return localHour >= 18 // 6pm or later
  }
  
  // Future dates are not overdue
  return false
}

/**
 * Initialize the timezone cache (call this on app startup)
 */
export const initializeTimezone = async (): Promise<void> => {
  cachedTimezone = await getConfiguredTimezone()
}

// Export all functions as default object too
export default {
  parseDate,
  formatDate,
  formatRelativeDate,
  formatTime,
  formatDateTime,
  isDateToday,
  isDatePast,
  isOverdue,
  isOverdueAsync,
  initializeTimezone
}

/**
 * Get days from today for a given date
 */
export const getDaysFromToday = (dateInput: any): number => {
  const date = parseDate(dateInput)
  if (!date) return 0
  
  const today = startOfDay(new Date())
  const targetDay = startOfDay(date)
  
  return differenceInDays(targetDay, today)
}

/**
 * Format event date (alias for formatRelativeDate)
 */
export const formatEventDate = formatRelativeDate

/**
 * Check if a string is a valid UUID
 */
export const isUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  return uuidRegex.test(str)
}

/**
 * Format a time string (HH:mm or HH:mm:ss) to 12-hour format
 * @param timeStr - Time string like "15:30" or "15:30:00"
 * @returns Formatted time like "3:30 PM"
 */
export const formatTimeString = (timeStr: string | null | undefined): string => {
  if (!timeStr) return ''

  const [hours, minutes] = timeStr.split(':')
  const hour = parseInt(hours, 10)
  const ampm = hour >= 12 ? 'PM' : 'AM'
  const hour12 = hour % 12 || 12
  return `${hour12}:${minutes} ${ampm}`
}

/**
 * Parse a date string (YYYY-MM-DD) as LOCAL time, not UTC
 *
 * CRITICAL: This function prevents timezone-related bugs where dates appear
 * on the wrong day due to UTC parsing and timezone offset shifts.
 *
 * WRONG: new Date('2024-10-24') → Parses as UTC midnight, shifts to local timezone
 * RIGHT:  parseLocalDate('2024-10-24') → Parses as local midnight, no shift
 *
 * Example timezone bug (Eastern Time, UTC-4):
 * - Date string: "2024-10-24"
 * - Wrong: new Date("2024-10-24") → Oct 23 8pm EDT (UTC 00:00 - 4 hours)
 * - Right: parseLocalDate("2024-10-24") → Oct 24 12am EDT (local 00:00)
 *
 * This bug has repeatedly caused issues with:
 * - Availability override dates appearing one day earlier
 * - Custody changeover days showing on wrong weekdays
 * - Calendar views displaying events on incorrect dates
 *
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns Date object in local timezone
 */
export function parseLocalDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}
